// Which site an address belongs to since the 25 Oct 2026 split
// (docs/localpickr/CONTRACT.md "Where an address goes"): the cross-site
// redirect map. This repo builds only www.offlico.com; the LocalPickr website
// (localpickr.com) has its own repo, Rithik-bot/localpickr, with its own copy
// of this file. Keep the two maps in step.
//   - www.offlico.com (offlico.com 301s to it): Offlico, the software for
//     businesses. Business pages live at root paths (/pricing/, /features/...),
//     plus the app, blog, templates, support and what a business sends its
//     customers (/book/<slug>, /c/<code>, /forms/<token>, /address/<token>).
//   - localpickr.com (www.localpickr.com 301s to it): LocalPickr, where
//     customers find and request local professionals (/, /how-it-works/,
//     /find/, /help/, /looking-for/..., /directories/..., /hub, customer
//     sign-in). On www these addresses go there (SiteDomainBoundary, and
//     Render's rules server side).
//   - localpickr.com/search/: LocalPickr's professional search, a separate page
//     (its own HTML entry). find.localpickr.com only redirects there.
//   - business.offlico.com and find.offlico.com only redirect now (Render does
//     it server side; the same mapping runs here in case the app loads there).
// Inside the app, business pages keep their /for-businesses/* addresses
// (internalMarketingPath); the browser and crawlers see the served addresses
// above. The localpickr.com branches below describe LocalPickr's own site (its
// copy of this map runs there).
import {
  LEGACY_BUSINESS_HOSTNAME,
  LEGACY_FIND_HOSTNAME,
  LOCALPICKR_FIND_HOSTNAME,
  LOCALPICKR_ORIGIN,
  LOCALPICKR_SEARCH_PATH,
  OFFLICO_ORIGIN,
  normalizeHostname,
} from "../brand/localpickr.js";
import { withoutLanguagePrefix } from "../i18n/languagePrefixes.js";
import { establishedProfessionIds, professionCatalogue } from "../pages/marketing/new-hub/professions/catalogue.js";

const OFFLICO = "offlico";
const LOCALPICKR = "localpickr";
const OFFLICO_HOSTNAMES = ["www.offlico.com", "offlico.com"];
const LOCALPICKR_SITE_HOSTNAMES = ["localpickr.com", "www.localpickr.com"];

export const BUSINESS_MARKETING_PATHS = [
  "/pricing", "/features", "/switch", "/all-mobile-businesses", "/professions", "/tools",
  "/chiropodists", "/chiropodist", "/mobile-hairdressers", "/mobile-hairdresser",
  "/cleaners", "/cleaner", "/gardeners", "/gardener",
  "/ear-wax-removal-specialists", "/ear-wax-removal-specialist",
  ...professionCatalogue.filter((profession) => !establishedProfessionIds.has(profession.id)).map((profession) => `/${profession.slug}`),
];
export const CUSTOMER_MARKETING_PATHS = ["/how-it-works", "/find", "/help"];
// Customer-side addresses: they belong on localpickr.com.
const LOCALPICKR_PATHS = ["/hub", "/looking-for", "/directories", "/customers", "/search", ...CUSTOMER_MARKETING_PATHS];
// The business app and its account pages.
const BUSINESS_APP_PATHS = ["/app", "/upgrade", "/tasks", "/shared-calendar", "/admin"];
// Everything else that only Offlico (www) serves.
const OFFLICO_PATHS = [
  ...BUSINESS_APP_PATHS,
  ...BUSINESS_MARKETING_PATHS,
  "/blog", "/templates", "/comparison", "/compare", "/vs", "/alternatives", "/what-is-offlico", "/support",
  "/book", "/c", "/forms", "/address", "/stripe",
  "/book-a-demo", "/about", "/for", "/switch-to-offlico", "/6-months-free", "/blog-downloads",
];

function normalizedPath(pathname) {
  return String(pathname || "/").replace(/\/+$/, "") || "/";
}

function isAtPath(pathname, path) {
  return pathname === path || pathname.startsWith(path + "/");
}

function isRedirectTo(redirect, paths) {
  return paths.some((path) => redirect === path || redirect.startsWith(path + "/") || redirect.startsWith(path + "?"));
}

function isCustomerAuth(searchParams) {
  const redirect = searchParams.get("redirect") || "";
  return searchParams.get("audience") === "customer" || isRedirectTo(redirect, ["/hub", "/lead-hub"]);
}

function isBusinessAuth(searchParams) {
  const redirect = searchParams.get("redirect") || "";
  return searchParams.get("audience") === "business" || isRedirectTo(redirect, BUSINESS_APP_PATHS);
}

// The site whose pages a host shows: "offlico", "localpickr", or null for
// hosts that are neither (localhost, Render previews).
function siteOfHost(hostname) {
  const host = normalizeHostname(hostname);
  if (OFFLICO_HOSTNAMES.includes(host) || host === LEGACY_BUSINESS_HOSTNAME) return OFFLICO;
  if (LOCALPICKR_SITE_HOSTNAMES.includes(host) || host === LEGACY_FIND_HOSTNAME) return LOCALPICKR;
  return null;
}

// Hosts that serve their site's pages themselves (no redirect needed when the
// address already belongs there). offlico.com is redirected to www by Render.
function isPrimaryHost(hostname, site) {
  const host = normalizeHostname(hostname);
  return site === OFFLICO ? OFFLICO_HOSTNAMES.includes(host) : host === "localpickr.com";
}

// Where an address on a site goes: { site, pathname }.
function resolveAddress(site, url) {
  const pathname = normalizedPath(url.pathname);
  // The business app embeds this same-origin preview frame. Sending it to
  // another host would break its parent-frame messaging and the locked booking UI.
  if (pathname === "/booking-preview-frame") return { site, pathname: url.pathname, stay: true };
  if (isAtPath(pathname, "/for-businesses")) {
    const suffix = url.pathname.slice("/for-businesses".length);
    return { site: OFFLICO, pathname: suffix && suffix !== "/" ? suffix : "/" };
  }
  if (isAtPath(pathname, "/for-customers")) {
    const suffix = url.pathname.slice("/for-customers".length);
    const destination = suffix && suffix !== "/" ? suffix : "/";
    return {
      site: LOCALPICKR,
      pathname: isAtPath(normalizedPath(destination), "/contact") ? destination.replace(/^\/contact/, "/help") : destination,
    };
  }
  if (isAtPath(pathname, "/lead-hub")) return { site: LOCALPICKR, pathname: "/hub" };
  if (LOCALPICKR_PATHS.some((path) => isAtPath(pathname, path))) return { site: LOCALPICKR, pathname: url.pathname };
  if (pathname === "/contact") {
    return site === LOCALPICKR ? { site: LOCALPICKR, pathname: "/help" } : { site: OFFLICO, pathname: url.pathname };
  }
  if (pathname === "/signin" || pathname === "/signup") {
    if (isCustomerAuth(url.searchParams)) return { site: LOCALPICKR, pathname: url.pathname };
    if (isBusinessAuth(url.searchParams)) return { site: OFFLICO, pathname: url.pathname };
    // Plain sign-in pages and legacy password-reset links (audience unknown)
    // stay on the site they were opened on.
    return { site, pathname: url.pathname };
  }
  if (OFFLICO_PATHS.some((path) => isAtPath(pathname, path)) || pathname.startsWith("/offlico-vs-")) {
    return { site: OFFLICO, pathname: url.pathname };
  }
  // The home page, /privacy, /terms and anything unknown stay on their site.
  return { site, pathname: url.pathname };
}

function siteOrigin(site) {
  return site === LOCALPICKR ? LOCALPICKR_ORIGIN : OFFLICO_ORIGIN;
}

// The address a page should be served at, or null when it is already there.
// Query strings and hashes are kept (customer hub links carry ?token=...).
export function canonicalProductUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  // find.localpickr.com only redirects: every address there goes to the search
  // page on localpickr.com, query kept (Render does it server side).
  if (normalizeHostname(url.hostname) === LOCALPICKR_FIND_HOSTNAME) {
    const search = new URL(LOCALPICKR_SEARCH_PATH + url.search + url.hash, LOCALPICKR_ORIGIN);
    return search.href;
  }
  const site = siteOfHost(url.hostname);
  if (!site) return null;

  const target = resolveAddress(site, url);
  if (target.stay) return null;
  if (target.site === site && target.pathname === url.pathname && isPrimaryHost(url.hostname, site)) return null;

  const destination = new URL(url.href);
  destination.protocol = "https:";
  destination.host = new URL(siteOrigin(target.site)).host;
  destination.pathname = target.pathname;
  return destination.href === url.href ? null : destination.href;
}

// "offlico" or "localpickr": which brand a host shows. Hosts that are neither
// (localhost, Render previews) show Offlico, the default build.
export function siteForHostname(hostname) {
  return siteOfHost(hostname) || OFFLICO;
}

export function isLocalPickrSiteHostname(hostname) {
  return siteOfHost(hostname) === LOCALPICKR;
}

export function isOfflicoSiteHostname(hostname) {
  return !isLocalPickrSiteHostname(hostname);
}

// The app's internal address for a page served at pathname on hostname:
// business pages are /for-businesses/*, customer pages /for-customers/*.
export function internalMarketingPath(pathname, hostname) {
  // /de/pricing/ is the German /pricing/ (docs/international/CONTRACT.md).
  const path = normalizedPath(withoutLanguagePrefix(pathname));
  if (isLocalPickrSiteHostname(hostname)) {
    if (path === "/") return "/for-customers";
    if (path === "/help" || path === "/contact") return "/for-customers/contact";
    if (CUSTOMER_MARKETING_PATHS.some((base) => isAtPath(path, base))) return "/for-customers" + path;
    return path;
  }
  if (path === "/") return "/for-businesses";
  if (path === "/contact") return "/for-businesses/contact";
  if (BUSINESS_MARKETING_PATHS.some((base) => isAtPath(path, base))) return "/for-businesses" + path;
  return path;
}

// Kept for the callers written before the split.
export const legacyMarketingPathForHost = internalMarketingPath;

// Pages that only LocalPickr shows (on any host, including localhost):
// customer marketing, looking-for and directory pages and the hub.
export function isLocalPickrPath(pathname) {
  const path = normalizedPath(String(pathname || "/").split(/[?#]/, 1)[0]);
  return isAtPath(path, "/for-customers") || isAtPath(path, "/lead-hub") || LOCALPICKR_PATHS.some((base) => isAtPath(path, base));
}

// The brand of a page: LocalPickr on LocalPickr's hosts and for its own
// pages, Offlico otherwise.
export function brandForPage(pathname, hostname) {
  return isLocalPickrSiteHostname(hostname) || isLocalPickrPath(pathname) ? LOCALPICKR : OFFLICO;
}

// LocalPickr's home page as a link from a page on hostname: "/" on LocalPickr's
// hosts, its address on the other production hosts, the app's internal
// address elsewhere (localhost, previews).
export function localPickrHomeHref(hostname) {
  if (isLocalPickrSiteHostname(hostname)) return "/";
  return siteOfHost(hostname) ? LOCALPICKR_ORIGIN + "/" : "/for-customers";
}

// Hosts that only serve customer (LocalPickr) pages.
export function isCustomerHostname(hostname) {
  return isLocalPickrSiteHostname(hostname);
}

// Production hosts of either site (not localhost or previews).
export function isProductHostname(hostname) {
  return siteOfHost(hostname) !== null;
}

// LocalPickr's search page (localpickr.com/search/) is a separate page with its
// own HTML entry: links to it must load the page, never navigate inside this app.
export function isLocalPickrSearchPath(pathname) {
  return isAtPath(normalizedPath(pathname), "/search");
}
